import Typography from '@mui/material/Typography';
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Minus, Plus, Search, Tag, X } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors,
  PopupMenu,
  PopupMenuButton,
  PopupMenuInput,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useForwardedRef } from 'shared/hooks/use-forwarded-ref';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { ID } from 'shared/types/id';
import { NewTagWithID, Tag as TagType } from 'shared/types/tag';
import { getAutoCompleteSuggestions } from 'shared/utils/get-auto-complete-suggestions';

import * as Styled from './tag-search-menu.style';

enum Direction {
  Next = 'next',
  Previous = 'previous',
}

export type TagSearchMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'ref' | 'children'
> & {
  tags: (TagType | NewTagWithID)[];
  value?: ID[] | null;
  indeterminateValue?: ID[] | null;
  onChange: (ids: ID[]) => void;
  onClear?: () => void;
  onCreate?: (name: string) => void;
  hideCreateButton?: boolean;
};

export const TagSearchMenu = forwardRef<HTMLDivElement, TagSearchMenuProps>(
  (
    {
      tags,
      value,
      indeterminateValue,
      onChange,
      onClear,
      onCreate,
      hideCreateButton,
      ...rest
    },
    ref,
  ) => {
    const containerRef = useForwardedRef(ref);
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement>(null);
    const [containerHeight, setContainerHeight] = useState<number>();

    const [searchValue, setSearchValue] = useState('');

    const _onCreate = (e: React.MouseEvent) => {
      e.preventDefault();
      onCreate?.(searchValue);
      setSearchValue('');
    };

    const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) =>
      setSearchValue(e.target.value);

    const _onClear = () => {
      if (onClear) {
        onClear();
        return;
      }
      onChange([]);
    };

    const _onChange = (id: ID) => {
      const oldValue = value ?? [];
      const newValue = oldValue.filter((tagId) => tagId !== id);
      if (oldValue.length === newValue.length) {
        newValue.push(id);
      }

      onChange(newValue);
    };

    const tagSuggestions = useMemo(
      () => getAutoCompleteSuggestions(searchValue, tags, ['name']),
      [searchValue, tags],
    );

    useEffect(() => {
      inputRef.current?.focus();
    }, []);

    useEffect(() => {
      setContainerHeight(
        Math.max(
          containerRef.current?.clientHeight ?? 0,
          Styled.popupMenuMinHeight,
        ),
      );
    }, [containerRef]);

    const focusItem = React.useCallback(
      (direction: Direction) => {
        if (!containerRef.current) {
          return;
        }

        const buttons = Array.from(
          containerRef.current.querySelectorAll('button:not([disabled])'),
        ) as HTMLElement[];

        // Include input in navigation cycle if needed, or handle separately.
        // If Button focused && Up/Down -> Navigate buttons.
        // If First Button focused && Up -> Focus Input.

        // Input is top.
        const activeElement = document.activeElement as HTMLElement;
        const isInputFocused = activeElement === inputRef.current;

        if (isInputFocused) {
          buttons[
            // If Input focused && Down -> Focus first button; focused && Up -> Focus last button
            direction === Direction.Next ? 0 : buttons.length - 1
          ]?.focus();
          return;
        }

        const currentIndex = buttons.indexOf(activeElement);

        if (currentIndex === -1) {
          return;
        }

        const newIndex =
          direction === Direction.Next
            ? currentIndex + 1 > buttons.length - 1
              ? 0
              : currentIndex + 1
            : currentIndex - 1 < 0
              ? // Going up from first button means focus on input
                undefined
              : currentIndex - 1;
        if (typeof newIndex === 'undefined') {
          // Focus Input
          inputRef.current?.focus();
          return;
        }

        buttons[newIndex].focus();
      },
      [containerRef],
    );

    useKeysDown(
      ['ArrowDown'],
      (e) => {
        e.stopPropagation();
        e.preventDefault();
        focusItem(Direction.Next);
      },
      { capture: true },
    );

    useKeysDown(
      ['ArrowUp'],
      (e) => {
        e.stopPropagation();
        e.preventDefault();
        focusItem(Direction.Previous);
      },
      { capture: true },
    );

    return (
      <Styled.PopupMenu ref={containerRef} $height={containerHeight} {...rest}>
        <PopupMenuList hasBottomBorder>
          <PopupMenuListItem>
            <PopupMenuInput
              ref={inputRef}
              startIcon={Search}
              value={searchValue}
              placeholder={t(
                onCreate
                  ? 'task.tags.search-or-create.placeholder'
                  : 'task.tags.search.placeholder',
              )}
              onChange={onChangeInput}
            />
          </PopupMenuListItem>
        </PopupMenuList>

        <PopupMenuList hasBottomBorder={!!value?.length}>
          {tagSuggestions.map(({ id, name, color }) => {
            const onSelectItem = () => _onChange(id);

            return (
              <PopupMenuListItem key={id}>
                <PopupMenuButton
                  start={
                    <Styled.IconContainer $color={color}>
                      <Icon icon={Tag} />
                    </Styled.IconContainer>
                  }
                  onClick={onSelectItem}
                  end={
                    value?.includes(id) ? (
                      <Styled.SelectedIconContainer>
                        <Icon icon={Check} />
                      </Styled.SelectedIconContainer>
                    ) : indeterminateValue?.includes(id) ? (
                      <Styled.SelectedIconContainer>
                        <Icon icon={Minus} />
                      </Styled.SelectedIconContainer>
                    ) : undefined
                  }
                  tabIndex={0}
                  overflowEllipsis
                >
                  {name}
                </PopupMenuButton>
              </PopupMenuListItem>
            );
          })}
          {!!onCreate && (!hideCreateButton || !!searchValue) && (
            <PopupMenuListItem>
              <PopupMenuButton
                start={<Icon icon={Plus} />}
                startColor={Colors.Primary}
                textColor={Colors.Primary}
                onClick={_onCreate}
                tabIndex={0}
              >
                {searchValue || t('forms.tag.create.title')}
              </PopupMenuButton>
            </PopupMenuListItem>
          )}
        </PopupMenuList>

        {!tagSuggestions.length &&
          (!onCreate || (hideCreateButton && !!searchValue)) &&
          !!searchValue && (
            <Styled.NoResultsContainer>
              <Typography variant="body2" color="text.secondary">
                {t('general.labels.no-results')}
              </Typography>
            </Styled.NoResultsContainer>
          )}

        {(!!value?.length || !!indeterminateValue?.length) && (
          <PopupMenuList>
            <PopupMenuListItem>
              <PopupMenuButton
                start={<Icon icon={X} />}
                startColor={Colors.Error}
                textColor={Colors.Error}
                onClick={_onClear}
                tabIndex={0}
              >
                {t('task.tags.options.remove.label', {
                  count:
                    (value?.length ?? 0) + (indeterminateValue?.length ?? 0),
                })}
              </PopupMenuButton>
            </PopupMenuListItem>
          </PopupMenuList>
        )}
      </Styled.PopupMenu>
    );
  },
);
